import { createPortal } from "react-dom";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import styles from "./Tooltip.module.css";

const HIDE_DELAY = 120;

export const Tooltip = () => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  const anchorRef = useRef<HTMLElement | null>(null);
  const hideTimerRef = useRef<number | null>(null);

  const [content, setContent] = useState<string | null>(null);
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: "0px", left: "0px" });
  const [customStyle, setCustomStyle] = useState({} as React.CSSProperties);

  const clearHideTimer = () => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  };

  const calculatePosition = () => {
    const anchor = anchorRef.current;
    const body = bodyRef.current;
    if (!anchor || !body) return;

    const rect = anchor.getBoundingClientRect();
    const arrowH = 6;

    const top = rect.top - body.offsetHeight - arrowH - 10;
    const left = rect.left + rect.width / 2 - body.offsetWidth / 2;

    setPosition({
      top: `${top}px`,
      left: `${left}px`,
    });
  };

  useLayoutEffect(() => {
    if (!content) return;

    calculatePosition();
    //eslint-disable-next-line
    setVisible(true);
  }, [content]);

  const onEnter = (e: MouseEvent) => {
    const target = e.target as Element | null;
    if (!target) return;

    const el = target.closest<HTMLElement>("[data-tooltip]");
    if (!el) return;

    const color = el.dataset.color;
    const bgColor = el.dataset.bgcolor;

    const colorText = { "--color": color ? color : "var(--global-bg-color)" } as React.CSSProperties;
    const colorBg = {
      "--bgColor": bgColor ? bgColor : "var(--global-color-link)",
    } as React.CSSProperties;
    setCustomStyle((prev) => ({ ...prev, ...colorText, ...colorBg }));

    const text = el.dataset.tooltip;
    if (!text) return;

    if (anchorRef.current && anchorRef.current !== el) {
      anchorRef.current = el;
      clearHideTimer();
      setContent(text);

      requestAnimationFrame(() => {
        calculatePosition();
      });

      return;
    }

    if (!anchorRef.current) {
      anchorRef.current = el;
      clearHideTimer();
      setVisible(false);
      setContent(text);
    }
  };

  const onLeave = (e: MouseEvent) => {
    const target = e.target as Element | null;
    const related = e.relatedTarget as Element | null;
    if (!target) return;

    const el = target.closest<HTMLElement>("[data-tooltip]");
    if (!el) return;

    if (related && related.closest("[data-tooltip]")) {
      return;
    }

    anchorRef.current = null;
    setVisible(false);

    clearHideTimer();
    hideTimerRef.current = window.setTimeout(() => {
      if (!anchorRef.current) {
        setContent(null);
      }
    }, HIDE_DELAY);
  };

  const clearTooltip = () => {
    anchorRef.current = null;
    setVisible(false);
    clearHideTimer();
    setContent(null);
  };

  useEffect(() => {
    document.addEventListener("mouseover", onEnter, true);
    document.addEventListener("mouseout", onLeave, true);
    document.addEventListener("click", clearTooltip, true);
    document.addEventListener("keydown", clearTooltip, true);

    return () => {
      document.removeEventListener("mouseover", onEnter, true);
      document.removeEventListener("mouseout", onLeave, true);
      document.removeEventListener("click", clearTooltip, true);
      document.removeEventListener("keydown", clearTooltip, true);
      clearHideTimer();
    };
  }, []);

  useEffect(() => {
    if (!content || !anchorRef.current) return;

    const anchor = anchorRef.current;
    if (!document.contains(anchor)) {
      //eslint-disable-next-line
      clearTooltip();
      return;
    }

    const interval = window.setInterval(() => {
      if (anchorRef.current && !document.contains(anchorRef.current)) {
        clearTooltip();
      }
    }, 100);

    return () => {
      window.clearInterval(interval);
    };
  }, [content]);

  if (!content) return null;

  return createPortal(
    <div
      ref={tooltipRef}
      className={`${styles.tooltip} ${visible ? styles.show : ""}`}
      style={{ ...position, ...customStyle }}
    >
      <div ref={bodyRef} className={styles.body}>
        {content}
      </div>
      <div className={styles.arrow} />
    </div>,
    document.body,
  );
};
